import { useState, useEffect } from 'react';
import seoPagesService from '@/services/seoPages';
import seoKeywordsService from '@/services/seoKeywords';
import seoInsightsService from '@/services/seoInsights';
import { GSCKeywordRow } from '@/services/searchConsole';
import { LivePositionResult } from '@/services/seoAi';

type GSCKeywordData = {
  dailyTrend: Array<{ date: string; clicks: number; impressions: number; ctr: number; position: number }>;
  rankingPages: Array<{ page: string; clicks: number; impressions: number; ctr: number; position: number }>;
  totals: { clicks: number; impressions: number; avgCtr: number; avgPosition: number };
};

export function usePageSync(
  projectId: string,
  gscData: GSCKeywordData | null,
  sitePages: GSCKeywordRow[],
  keywordId: string | undefined,
  livePosition: LivePositionResult | null
) {
  // Sync GSC pages to seo_pages table when data arrives
  const [pagesSynced, setPagesSynced] = useState(false);
  useEffect(() => {
    if (!projectId || pagesSynced || sitePages.length === 0) return;
    setPagesSynced(true);

    const uniquePages = new Map<string, { url: string }>();
    for (const sp of sitePages) {
      if (sp.page && !uniquePages.has(sp.page)) {
        uniquePages.set(sp.page, { url: sp.page });
      }
    }
    if (gscData?.rankingPages) {
      for (const rp of gscData.rankingPages) {
        if (rp.page && !uniquePages.has(rp.page)) {
          uniquePages.set(rp.page, { url: rp.page });
        }
      }
    }

    if (uniquePages.size > 0) {
      seoPagesService.bulkUpsertFromGSC(projectId, Array.from(uniquePages.values())).catch(() => {});
    }
  }, [projectId, sitePages, gscData, pagesSynced]);

  // Persist GSC daily data as keyword snapshots
  const [snapshotsSaved, setSnapshotsSaved] = useState(false);
  useEffect(() => {
    if (!keywordId || snapshotsSaved || !gscData?.dailyTrend?.length) return;
    setSnapshotsSaved(true);
    seoKeywordsService.saveGSCSnapshots(keywordId, gscData.dailyTrend).catch(() => {});
  }, [keywordId, gscData, snapshotsSaved]);

  // Auto-measure pending WP changes when GSC data loads
  useEffect(() => {
    if (!projectId || !gscData?.totals) return;
    const pos = livePosition?.livePosition ?? gscData.totals.avgPosition;
    seoInsightsService
      .measurePendingChanges(projectId, {
        position: pos,
        ctr: gscData.totals.avgCtr,
        clicks: gscData.totals.clicks,
        impressions: gscData.totals.impressions,
      })
      .catch(() => {});
  }, [projectId, gscData, livePosition]);
}
